import { formatHumanName } from '@medplum/core'
import { useMedplum } from '@medplum/react'

import type { DocumentReference, Patient } from '@medplum/fhirtypes'
import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { generateCareCoordinatorCDA } from '@/pages/Call/components/soap.utils.ts'
import { getReasonForCallLabel, type CareCoordinatorNoteFormValues } from './careCoordinatorSchema'
import { resolveChartVisitContext } from './progressNoteEncounter.utils'

const CARE_COORDINATOR_NOTE_DATA_URL =
  'http://behuman.com/fhir/StructureDefinition/care-coordinator-note-data'

export function useUpdateCareCoordinatorNote(
  patient: Patient,
  documentReference: DocumentReference,
  onSaved: () => void,
) {
  const medplum = useMedplum()

  return useMutation({
    async mutationFn(values: CareCoordinatorNoteFormValues) {
      if (!patient.id) {
        throw new Error('Patient id is required')
      }
      const context = await resolveChartVisitContext(medplum, patient)

      const profile = medplum.getProfile()
      if (!profile) {
        throw new Error('Signed-in practitioner profile is required')
      }

      const cdaXml = generateCareCoordinatorCDA({
        patient,
        profile,
        values,
        noteDate: documentReference.date,
        practitionerDisplay: context.practitionerDisplay,
      })

      const attachment = await medplum.createAttachment({
        contentType: 'application/cda+xml',
        data: cdaXml,
      })

      const updatedExtensions = [
        ...(documentReference.extension?.filter((e) => e.url !== CARE_COORDINATOR_NOTE_DATA_URL) ??
          []),
        { url: CARE_COORDINATOR_NOTE_DATA_URL, valueString: JSON.stringify(values) },
      ]

      await medplum.updateResource<DocumentReference>({
        ...documentReference,
        authenticator: {
          reference: `Practitioner/${context.practitionerId}`,
          display: context.practitionerDisplay || undefined,
        },
        subject: {
          reference: `Patient/${patient.id}`,
          display: patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined,
        },
        description: `Care Coordinator Note – ${getReasonForCallLabel(values.reasonForCall)}`,
        content: [
          {
            attachment,
            format: {
              system: 'http://behuman.com/fhir/document-format',
              code: 'care-coordinator-note-v1',
              display: 'Care Coordinator Note (C-CDA)',
            },
          },
        ],
        extension: updatedExtensions,
      })
    },
    onSuccess() {
      showNotification({ color: 'green', message: 'Care Coordinator note updated' })
      onSaved()
    },
    onError(err) {
      logError('patient', 'Update Care Coordinator note', err)
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Could not update Care Coordinator note',
      })
    },
  })
}
